/**
 * messageSendHandlers.js
 *
 * Extracted from the ~1,880 lines of inline <script> that used to sit
 * directly inside src/index.html (one block before the <html> tag, a
 * second smaller block inside <head>). This file is loaded via a normal
 * <script src="legacy/messageSendHandlers.js"> tag from index.html instead.
 *
 * Why this is safe to just move: the manifest's WebViewRuntime.Url /
 * JSRuntime.Url / Commands.Url / Compose.Taskpane.Url / Read.Taskpane.Url
 * all point at this same index.html (Outlook's "shared runtime" model), so
 * this script keeps running in exactly the same process/global scope as
 * before - only where the source lives changed.
 *
 * The ONLY functional changes from the original inline version:
 *   1. The three spots that read `localStorage.getItem("authToken")`
 *      directly now call `await window.ClarraAuth.getAccessToken()`
 *      instead - that function (exposed by src/services/tokenManager.ts,
 *      part of the same shared runtime/global scope) returns the current
 *      in-memory access token, transparently refreshing it first via the
 *      Public API if it's expired.
 *   2. extractTenantIdFromToken()/getTenantAndUserIdsFromToken() decode
 *      claims from window.ClarraAuth.getDecodedAccessToken() instead of
 *      re-parsing a JWT out of localStorage (same claims, same JWT -
 *      tokenManager just doesn't put it in localStorage anymore).
 *   3. CallfromPopup(...) - a legacy fallback for a plain `window.open()`
 *      popup flow (as opposed to the Office dialog flow LoginComponents.tsx
 *      actually uses) - now receives an authorization `code` rather than a
 *      token (see assets/callback.js) and can't complete a login on its own
 *      (it doesn't have the matching PKCE code_verifier, which only
 *      LoginComponents.tsx's handleLogin holds), so it no longer writes
 *      anything to localStorage; it logs and no-ops. Nothing in this
 *      codebase currently drives a plain `window.open()` to callback.html
 *      (the real path is Office.context.ui.displayDialogAsync +
 *      DialogMessageReceived in LoginComponents.tsx), so this is dead-code
 *      parity, not a functional regression.
 *
 * Everything else - form validation, attachment handling, payload
 * construction, onMessageSendHandler and its historical variants, blob
 * upload helpers - is unchanged business logic, moved verbatim.
 */

async function SaveCommunicationDirect_IE(Cdata, Event) {
  const API_BASE_URL = localStorage.getItem("API_BASE_URL");
  const CreateCommunicationsform = localStorage.getItem("CreateCommunicationsform");
  const AccessToken = await window.ClarraAuth.getAccessToken();
  if (!Cdata) {
    OutLookAlert(Event, false, "Invalid input data");
    return false;
  }
  if (!AccessToken) {
    OutLookAlert(Event, false, "Unable to retrieve AccessToken. Please try again.");
    return false;
  }
  if (!CreateCommunicationsform) {
    OutLookAlert(Event, false, "Unable to process. Please try again.");
    return false;
  }

  // Convert attachments to single files
  let index = 1;
  Cdata.UploadFileAttachments.forEach(element => {
    if (element.filename !== "") {
      let file = new File([element.blobfile], element.filename);
      switch (index) {
        case 1: Cdata.UploadSingleAttachments1 = file; break;
        case 2: Cdata.UploadSingleAttachments2 = file; break;
        case 3: Cdata.UploadSingleAttachments3 = file; break;
        case 4: Cdata.UploadSingleAttachments4 = file; break;
        case 5: Cdata.UploadSingleAttachments5 = file; break;
        case 6: Cdata.UploadSingleAttachments6 = file; break;
      }
      index++;
    }
  });

  // Construct FormData manually
  Cdata.createClarraDocs = !!Cdata.createClarraDocs; // will always be true or false
  Cdata.CreateCommunication = !!Cdata.createCommunication;
  Cdata.CreateTimeSlip = !!Cdata.createTimeslip;


  let formData = new FormData();
  for (let key in Cdata) {
    if (Object.prototype.hasOwnProperty.call(Cdata, key)) {
      let value = Cdata[key];
      if (value instanceof File) {
        formData.append(key, value, value.name);
      } else if (value instanceof Date) {
        formData.append(key, value.toISOString()); //  send clean datetime
      } else if (typeof value === "boolean" || typeof value === "number" || typeof value === "string") {
        formData.append(key, value); // send primitive types as-is
      } else if (Array.isArray(value) || typeof value === "object") {
        formData.append(key, JSON.stringify(value)); // only JSON complex objects
      }
    }
  }

  // IE-compatible XMLHttpRequest
  try {
    var xhr = new XMLHttpRequest();
    xhr.open("POST", API_BASE_URL + CreateCommunicationsform, true);
    xhr.setRequestHeader("Authorization", "Bearer " + AccessToken);
    xhr.setRequestHeader("Accept", "*/*");

    xhr.onreadystatechange = function () {
      if (xhr.readyState === 4) {
        if (xhr.status === 200 || xhr.status === 201) {
          localStorage.removeItem("attachments");
          sessionStorage.removeItem("attachments");
          localStorage.removeItem("formData");
          localStorage.removeItem("SelectedMatter");
          OutLookAlert(Event, null, true);
        } else {
          OutLookAlert(Event, xhr.responseText, false);
        }
      }
    };

    xhr.send(formData);

  } catch (error) {
    OutLookAlert(Event, "XHR failed: " + error.message, false);
  }
}

const blobToArrayBuffer = async (blob) => {
  return await blob.arrayBuffer();
}

async function commitBlocks(uploadUrl, blockIds) {
  const xml =
    `<?xml version="1.0" encoding="utf-8"?><BlockList>` +
    blockIds.map(id => `<Latest>${id}</Latest>`).join("") +
    `</BlockList>`;

  await fetch(`${uploadUrl}&comp=blocklist`, {
    method: "PUT",
    body: xml
  });
}
async function uploadBlobChunks(uploadUrl, blob, CHUNK_SIZE, onProgress) {

  // debugger;
  const blockIds = [];
  let offset = 0;
  let index = 0;

  while (offset < blob.size) {

    const chunk = blob.slice(offset, offset + CHUNK_SIZE);

    const blockId = btoa(index.toString().padStart(6, "0"));
    blockIds.push(blockId);

    await fetch(
      `${uploadUrl}&comp=block&blockid=${encodeURIComponent(blockId)}`,
      {
        method: "PUT",
        headers: { "x-ms-blob-type": "BlockBlob" },
        body: chunk
      }
    );

    if (onProgress) onProgress(chunk.size);

    offset += CHUNK_SIZE;
    index++;
  }

  return blockIds;
}

// File chunk is disabled for releasing Sprint 116

async function SaveCommunicationDirect(Cdata, Event, uiDictionary) {
  localStorage.setItem("composeUploadProgress", "0");
  const API_BASE_URL = localStorage.getItem("API_BASE_URL");
  const UploadURL = localStorage.getItem("UploadURL");
  const IngnoreFileExtensions = localStorage.getItem("IngnoreFileExtensions");
  const CreateCommunicationsform = localStorage.getItem("CreateCommunicationsform");
  console.log("AccessToken token before call SaveCommunicationDirect:");
  const AccessToken = await window.ClarraAuth.getAccessToken();
  console.log("AccessToken in SaveCommunicationDirect:", AccessToken);

  let index = 1;
  if (Cdata == null) {
    OutLookAlert(Event, false, "Invalid input data")
    return false;
  }
  if (AccessToken == null) {
    OutLookAlert(Event, false, "Unable to retreive AccessToken. Please try again.")
    return false;
  }

  if (CreateCommunicationsform == null) {
    OutLookAlert(Event, false, "Unable to process. Please try again.")
    return false;
  }
  let IngnoreFileUpload = false;
  if (Cdata.CreateClarraDocs == null)
    Cdata.CreateClarraDocs = false
  if (Cdata.CreateCommunication == false && Cdata.CreateClarraDocs == false)
    IngnoreFileUpload = true;

  const CHUNK_SIZE = 4 * 1024 * 1024; // 4MB
  let uploadedFiles = [];
  const IgnoreFileExtensionsSet = new Set(
    IngnoreFileExtensions.split(",").map(ext => ext.trim().toLowerCase())
  );

  // Validation Not supported file
  if (!IngnoreFileUpload && IngnoreFileExtensions != null && Cdata.UploadFileAttachments !== null) {
    for (const file of Cdata.UploadFileAttachments) {
      const extension = file.filename.split(".").pop()?.toLowerCase();
      if (extension && IgnoreFileExtensionsSet.has(extension)) {
        let fileerrormsg = "Clarra does not allow " + extension.toUpperCase() + " or other executable files to ensure platform security";
        OutLookAlert(Event, fileerrormsg, false);
        return false;
      }
    }
  }


  //newely added
  const attachments = Cdata.UploadFileAttachments || [];
  const totalSize = attachments.reduce((sum, f) => sum + (f.blobfile ? f.blobfile.size : 0), 0);
  let uploadedSize = 0;
  //

  if (!IngnoreFileUpload && Cdata.UploadFileAttachments?.length) {
    for (const element of Cdata.UploadFileAttachments) {
      if (!element?.filename) continue;
      var UploadURLRequest = {
        "filename": element.filename,
        "CreateCommunication": Cdata.CreateCommunication,
        "CreateTimeSlip": Cdata.CreateTimeSlip,
        "CreateClarraDocs": (Cdata.CreateClarraDocs) ? Cdata.CreateClarraDocs : false,
        "Matter": Cdata.Matter
      }

      let uploadUrl;
      let blobname;
      try {
        const { data } = await axios.post(API_BASE_URL + UploadURL, UploadURLRequest, {
          headers: {
            Authorization: `Bearer ${AccessToken}`,
            'Content-Type': "application/json",
            'mode': 'outlook'
          },
        }
        );

        uploadUrl = data.uploadUrl;
        blobname = data.blobname;

      }
      catch (error) {
        localStorage.setItem("composeloader", "1");
        const errorMessage =
          error?.response?.data?.error ||
          error?.response?.data?.message ||
          "Failed to fetch Document upload Url";

        if (error?.response?.status === 400) {
          OutLookAlert(Event, errorMessage, false);
        } else {
          Office.context.mailbox.item.notificationMessages.addAsync(
            "validationError",
            {
              type: "errorMessage",
              message: errorMessage
            }
          );

          Event.completed({ allowEvent: false });
        }

        return true;
      }

      // Upload actual file
      // const blockIds = await uploadBlobChunks(uploadUrl, element.blobfile, CHUNK_SIZE);
      //newely added
      const blockIds = await uploadBlobChunks(uploadUrl, element.blobfile, CHUNK_SIZE, (chunkSize) => {
        uploadedSize += chunkSize;
        const progress = Math.round((uploadedSize / totalSize) * 100);

        // 3. Write to localStorage to trigger listener in TaskPane
        localStorage.setItem("composeUploadProgress", progress);
      });
      //
      await commitBlocks(uploadUrl, blockIds);

      uploadedFiles.push({
        Id: index++,
        DownloadURL: `blob://${blobname}`,
        Name: element.filename
      });
    }

    Cdata.ZapierDownloadURL = (uploadedFiles != null && uploadedFiles.length > 0) ? uploadedFiles : null;
    delete Cdata.UploadFileAttachments;

    if (Cdata.CreateClarraDocs !== null && Cdata.CreateClarraDocs == true) {
      Cdata.ClarraDocs.ZapierDownloadURL = (uploadedFiles != null && uploadedFiles.length > 0) ? uploadedFiles : null;;

      if (Cdata.ClarraDocs != null)
        delete Cdata.ClarraDocs.UploadFileAttachments;
    }
  }
  else {
    delete Cdata.UploadFileAttachments;
    if (Cdata.ClarraDocs != null)
      delete Cdata.ClarraDocs.UploadFileAttachments;
  }
  //newely added
  localStorage.setItem("composeUploadProgress", "0");
  try {
    const response = await axios.post(API_BASE_URL + CreateCommunicationsform, Cdata, {
      headers: {
        Authorization: `Bearer ` + AccessToken,
        //'Content-Type': 'application/json',
        'Content-Type': 'multipart/form-data',
        Accept: '*/*',
        'mode': 'outlook'
      },
    });

    localStorage.removeItem("attachments");
    sessionStorage.removeItem("attachments");
    localStorage.removeItem("formData");
    localStorage.removeItem("SelectedMatter");


    var cid;
    var timeSlipId;
    var clarraDocsId;

    if (response.data != null)
      cid = response.data.id;
    if (response.data != null)
      timeSlipId = response.data.timeSlipId;
    if (response.data != null)
      clarraDocsId = response.data.mailLogId;
    sendWithHeader(Event, cid, timeSlipId, clarraDocsId);
    return response.data;

  }
  catch (error) {
    //newely added
    localStorage.setItem("composeUploadProgress", "0");
    localStorage.setItem("composeloader", "1");
    //
    let apierrorMessage = error?.response?.data?.error || "Invalid request";
    if (apierrorMessage == "Invalid request") {
      apierrorMessage = err?.response?.data || "Invalid request"
    }
    const backendErrorMap = JSON.parse(localStorage.getItem("BACKEND_ERROR_MAP") || "{}");

    if (uiDictionary && Object.keys(uiDictionary).length > 0) {


      Object.keys(backendErrorMap).forEach(errorWord => {
        const dictionaryKey = backendErrorMap[errorWord];
        const uiLabel = uiDictionary[dictionaryKey];
        if (uiLabel) {
          const regex = new RegExp(errorWord, "gi");
          apierrorMessage = apierrorMessage.replace(regex, uiLabel);
        }
      });


      Object.keys(uiDictionary).forEach(key => {
        const exceptionKeys = Object.values(backendErrorMap);
        if (!exceptionKeys.includes(key)) {
          const uiLabel = uiDictionary[key];
          const regex = new RegExp(key, "gi");
          apierrorMessage = apierrorMessage.replace(regex, uiLabel);
        }
      });
    }


    if (error && error.status === 400) {
      OutLookAlert(Event, apierrorMessage, false);
    } else {
      Office.context.mailbox.item.notificationMessages.addAsync("validationError", {
        type: "errorMessage",
        message: apierrorMessage
      });
      Event.completed({ allowEvent: false });
    }
    return true;
  }


}

async function SaveCommunicationDirectLegacy(Cdata, Event, uiDictionary) {

  const API_BASE_URL = localStorage.getItem("API_BASE_URL");
  const CreateCommunicationsform = localStorage.getItem("CreateCommunicationsform");
  const AccessToken = await window.ClarraAuth.getAccessToken();

  let index = 1;
  if (Cdata == null) {
    OutLookAlert(Event, false, "Invalid input data")
    return false;
  }
  if (AccessToken == null) {
    OutLookAlert(Event, false, "Unable to retreive AccessToken. Please try again.")
    return false;
  }

  if (CreateCommunicationsform == null) {
    OutLookAlert(Event, false, "Unable to process. Please try again.")
    return false;
  }

  Cdata.UploadFileAttachments.forEach(element => {
    if (element.filename != "") {
      let file = new File([element.blobfile], element.filename);
      switch (index) {
        case 1: Cdata.UploadSingleAttachments1 = file; break;
        case 2: Cdata.UploadSingleAttachments2 = file; break;
        case 3: Cdata.UploadSingleAttachments3 = file; break;
        case 4: Cdata.UploadSingleAttachments4 = file; break;
        case 5: Cdata.UploadSingleAttachments5 = file; break;
        case 6: Cdata.UploadSingleAttachments6 = file; break;
      }
      index = index + 1;
    }
  });

  try {
    const response = await axios.post(API_BASE_URL + CreateCommunicationsform, Cdata, {
      headers: {
        Authorization: `Bearer ` + AccessToken,
        'Content-Type': 'multipart/form-data',
        Accept: '*/*',
        'mode': 'outlook'
      },
    });

    localStorage.removeItem("attachments");
    sessionStorage.removeItem("attachments");
    localStorage.removeItem("formData");
    localStorage.removeItem("SelectedMatter");


    var cid;
    var timeSlipId;
    var clarraDocsId;

    if (response.data != null)
      cid = response.data.id;
    if (response.data != null)
      timeSlipId = response.data.timeSlipId;
    if (response.data != null)
      clarraDocsId = response.data.mailLogId;
    sendWithHeader(Event, cid, timeSlipId, clarraDocsId);
    return response.data;

  } catch (error) {
    let apierrorMessage = error?.response?.data?.error || "Something went wrong";
    const backendErrorMap = JSON.parse(localStorage.getItem("BACKEND_ERROR_MAP") || "{}");

    if (uiDictionary && Object.keys(uiDictionary).length > 0) {


      Object.keys(backendErrorMap).forEach(errorWord => {
        const dictionaryKey = backendErrorMap[errorWord];
        const uiLabel = uiDictionary[dictionaryKey];
        if (uiLabel) {
          const regex = new RegExp(errorWord, "gi");
          apierrorMessage = apierrorMessage.replace(regex, uiLabel);
        }
      });


      Object.keys(uiDictionary).forEach(key => {
        const exceptionKeys = Object.values(backendErrorMap);
        if (!exceptionKeys.includes(key)) {
          const uiLabel = uiDictionary[key];
          const regex = new RegExp(key, "gi");
          apierrorMessage = apierrorMessage.replace(regex, uiLabel);
        }
      });
    }


    if (error && error.status === 400) {
      OutLookAlert(Event, apierrorMessage, false);
    } else {
      Office.context.mailbox.item.notificationMessages.addAsync("validationError", {
        type: "errorMessage",
        message: apierrorMessage
      });
      Event.completed({ allowEvent: false });
    }
    return true;
  }


}


function extractTenantIdFromToken() {
  const decoded = window.ClarraAuth ? window.ClarraAuth.getDecodedAccessToken() : null;
  if (!decoded) return '';
  return (
    decoded["https://clarra/tenantId"] ||
    decoded.tid ||
    decoded.tenantId ||
    ''
  );
}

function getTenantAndUserIdsFromToken() {
  const decoded = window.ClarraAuth ? window.ClarraAuth.getDecodedAccessToken() : null;
  let tenantId = '', userId = '';
  if (decoded) {
    tenantId = decoded["https://clarra/tenantId"] || decoded.tid || decoded.tenantId || '';
    userId = decoded["https://clarra/userId"] || decoded.oid || decoded.sub || decoded.userId || '';
  }
  return { tenantId, userId };
}

function sendWithHeader(event, cid, timeSlipId, clarraDocsId) {
  const { tenantId, userId } = getTenantAndUserIdsFromToken();

  if (!tenantId || !userId) {
    localStorage.setItem("composeloader", "1");
    console.warn("Missing tenant or user info");
    event.completed({ allowEvent: true });
    return;
  }

  // Function to set custom properties (always execute this)
  const setCustomProperties = () => {
    Office.context.mailbox.item.loadCustomPropertiesAsync(res => {
      if (res.status === Office.AsyncResultStatus.Succeeded) {
        const props = res.value;

        const tenantId = extractTenantIdFromToken();

        // Set communication ID (handle both null and actual ID)
        props.set(`communicationId${tenantId}`, cid ? cid.toString() : "null");
        console.log(`[DEBUG] Set communicationId${tenantId}:`, cid);

        // Set timeSlip ID (handle both null and actual ID)
        props.set(`timeSlipId${tenantId}`, timeSlipId ? timeSlipId.toString() : "null");
        console.log(`[DEBUG] Set timeSlipId${tenantId}:`, timeSlipId);

        // Set clarraDocs ID (handle both null and actual ID)
        props.set(`clarraDocsId${tenantId}`, clarraDocsId ? clarraDocsId.toString() : "null");
        console.log(`[DEBUG] Set clarraDocsId${tenantId}:`, clarraDocsId);

        props.saveAsync(saveRes => {
          if (saveRes.status === Office.AsyncResultStatus.Succeeded) {
            console.log("Successfully saved extracted data to custom properties");
          } else {
            console.error("Failed to save extracted data to custom properties:", saveRes.error);
          }
          localStorage.setItem("composeloader", "1");
          event.completed({ allowEvent: true });
        });
      } else {
        localStorage.setItem("composeloader", "1");
        console.error("Failed to load custom properties:", res.error);
        event.completed({ allowEvent: true });
      }
    });
  };

  // Only set header if cid exists, but always set custom properties
  if (cid) {
    const headerValue = `tenantid=${tenantId}, userid=${userId}, ComId=${cid}`;

    Office.context.mailbox.item.internetHeaders.setAsync(
      { "X-Clarra-Tenant-User": headerValue },
      function (asyncResult) {
        if (asyncResult.status === Office.AsyncResultStatus.Succeeded) {
          console.log("Header set successfully");
        } else {
          console.warn("Header failed to set", asyncResult.error);
        }
        // Always set custom properties after header attempt
        setCustomProperties();
      }
    );
  } else {
    console.log("No communication ID, skipping header but setting custom properties");
    // Set custom properties even when no cid
    setCustomProperties();
  }
}

let item;
async function getSubject() {
  item = Office.context.mailbox.item;
  await item.subject.getAsync((asyncResult) => {
    if (asyncResult.status === Office.AsyncResultStatus.Failed) {
      return;
    }
    return asyncResult.value;
    // Display the subject on the page.

  });
}
async function getTo() {
  item = Office.context.mailbox.item;
  await item.to.getAsync((asyncResult) => {
    if (asyncResult.status === Office.AsyncResultStatus.Failed) {

      return;
    }

    return asyncResult.value;
  });
}

async function CallfromPopup(code) {
  // Legacy fallback for a plain `window.open()` popup flow, as opposed to
  // the Office.context.ui.displayDialogAsync flow LoginComponents.tsx
  // actually uses (which handles the authorization code via
  // processAuthCode/establishSession directly). Nothing in this codebase
  // currently drives a plain window.open() to callback.html, so this is
  // kept only for parity with assets/callback.js's
  // `window.opener.CallfromPopup(code)` call. It intentionally does not
  // write anything to localStorage - establishing a session requires the
  // matching PKCE code_verifier, which only handleLogin (LoginComponents.tsx)
  // holds, so a bare code arriving here can't be completed on its own.
  console.warn("CallfromPopup received an authorization code via the legacy popup path; ignoring (no PKCE verifier available here). Use the Office dialog login flow instead.");
}

async function Validation(event, Successflag, msg) {
  if (Successflag == false) {
    Successflag = false;
    Office.context.mailbox.item.notificationMessages.addAsync("validationError", {
      type: "errorMessage",
      message: msg
    });
  }

  if (!Successflag) {
    localStorage.setItem("composeloader", "1");

    event.completed({ allowEvent: false }); // Cancel send
    sessionStorage.setItem("mode", "1");
  } else {
    localStorage.setItem("composeloader", "1");
    event.completed({ allowEvent: true }); // Continue send
  }
}
async function OutLookAlert(event, message, successFlag) {
  try {
    localStorage.setItem("composeloader", "1");
    if (!successFlag) {
      message = (message || "Operation failed.")
        .replace(
          "Email was not logged to Clarra.",
          "Failed to log."
        );
      // Remove existing notification if present
      Office.context.mailbox.item.notificationMessages.removeAsync(
        "validationError",
        () => {
          Office.context.mailbox.item.notificationMessages.addAsync(
            "validationError",
            {
              type: Office.MailboxEnums.ItemNotificationMessageType.ErrorMessage,
              message: message
            },
            () => {
              // Cancel send after notification is added
              event.completed({ allowEvent: false });
            }
          );
        }
      );
      return;
    }
    // Success case
    event.completed({ allowEvent: true });
  }
  catch (ex) {
    // Fail safe - prevent send if something unexpected happens
    event.completed({ allowEvent: false });
  }
}

let attachmentParam = [];

function getLocalStorageItem(key) {
  const value = localStorage.getItem(key);
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

async function triggerReactFunction(data) {
  if (window.reactFunctionHandler) {
    let result = await window.reactFunctionHandler(data);
    return result;
  } else {
    return null;
  }
}
const validateForm = (formData) => {

  let isValid = true;
  if (formData.createCommunication) {
    if (!formData.matter) {
      isValid = false;
    }
  }
  if (formData.createTimeslip) {
    isValid = (!isValid) ? isValid : true;
    if (!formData.matterts) {
      isValid = false;
    }
    if (!formData.datets) {
      isValid = false;
    }
    var TimeSlipSetting = getLocalStorageItem("TimeSlipSetting");
    if (TimeSlipSetting) {
      if (TimeSlipSetting != null && TimeSlipSetting.zeroDurationType != null && TimeSlipSetting.zeroDurationType == 3) {
        try {
          if (formData.duration.trim() != "") {
            if (formData.duration.trim().split(".").length == 2) {
              const _d = Number(formData.duration.trim());
              if (_d <= 0) {
                isValid = false;
              }
            }
            else {
              const _d = Number(formData.duration.trim());
              if (_d <= 0) {
                isValid = false;
              }
            }
          }
          else {
            if (!formData.duration || formData.duration.trim() == "" || formData.duration == "") {
              isValid = false;
            }
          }
        }
        catch (error) {
          formData.duration = "";
          //tempErrors.duration = "Valid duration is required hours.minutes";
          isValid = false;
        }
      }
    }
    if (!formData.activityCode) {
      isValid = false;
    }
    if (!formData.taskCode) {
      isValid = false;
    }
    if (!formData.timekeeper) {
      isValid = false;
    }
    if (formData.description.length == 0) {
      isValid = false;
    }
  }
  if (formData.createClarraDocs) {
    isValid = (!isValid) ? isValid : true;
    if (!formData.datedoc) {
      isValid = false;
    }
    if (!formData.matterdoc) {
      isValid = false;
    }

    if (formData.tagdoc.length === 0) {
      isValid = false;
    }

    if (!formData.status) {
      isValid = false;
    }
    if (!formData.serviceType) {
      isValid = false;
    }
    if (formData.descriptiondoc.length == 0) {
      isValid = false;
    }
  }
  return isValid;
};



function getDefaultUiDictionary() {
  return {
    Communication: "Communication",
    Matter: "Matter",
    Tags: "Tags",
    Timekeeping: "Timeslip",
    Duration: "Duration",
    Timekeeper: "Timekeeper",
    ActivityCode: "Activity Code",
    TaskCode: "Task Code",
    Description: "Description",
    Note: "Note",
    ClarraDocs: "ClarraDocs",
    Status: "Status",
    MailTypes: "Service Type",
    Attachment: "Attachment",
    Docketing: "Docketing",
  };
}

//Kept the below code
async function onMessageSendHandler_Obseleted(event) {
  console.log("onMessageSendHandler Common Request");
  const isClassicOutlook = localStorage.getItem("isClassicOutlook") === "1";
  const isMacOutlook = localStorage.getItem("isMacOutlook") === "1";
  const IsDektop = localStorage.getItem("hostName") === "1";

  console.log("onMessageSendHandler called");
  const isPaneAlive = await new Promise(resolve => {

    const currentItemId = Office.context.mailbox.item?.itemId || "unsaved";

    const challengeToken =
      challenge - `{currentItemId}-${Date.now()}-${Math.random()}`;

    const handleResponse = (e) => {
      if (e.key === 'onSendResponse' && e.newValue === challengeToken) {
        cleanup();
        resolve(true);
      }
    };
    // If the task pane doesn't respond in time, we assume it's closed.
    const timeoutId = setTimeout(async () => {
      cleanup();
      resolve(false); // The pane is not alive.
    }, 300); // 300ms is a very generous wait time.

    // A helper to remove listeners and timers to prevent memory leaks.
    const cleanup = () => {
      clearTimeout(timeoutId);
      window.removeEventListener('storage', handleResponse);
      // Clean up the keys from localStorage.
      localStorage.removeItem('onSendChallenge');
      localStorage.removeItem('onSendResponse');
    };

    // Start listening for the response.
    window.addEventListener('storage', handleResponse);
    // Issue the challenge by writing the token to localStorage.
    localStorage.setItem('currentSendingItem', currentItemId);
    localStorage.setItem('onSendChallenge', challengeToken);
  });

  if (!isMacOutlook) {
    if (!isPaneAlive) {
      // The pane did not respond. Send the email and do nothing else.
      event.completed({ allowEvent: true });
      console.log("Email Sent");
      return;
    }
  }
  else {
    if (!isPaneAlive && !IsDektop) {
      // The pane did not respond. Send the email and do nothing else.
      event.completed({ allowEvent: true });
      console.log("Email Sent");
      return;
    }
  }
  var formdata = getLocalStorageItem("formData");

  const modeC = localStorage.getItem("mode");

  if (modeC == null || modeC == "0") {
    event.completed({ allowEvent: true });
    return true;
  }
  else {

    var isvalid = validateForm(formdata);

    formdata.isValid = isvalid;
  }


  if (formdata.isValid == false) {
    msg = "Mandatory fields are required.";
    Office.context.mailbox.item.notificationMessages.addAsync("validationError", {
      type: "errorMessage",
      message: msg
    });
    event.completed({ allowEvent: false });
    return true;
  }

  if ((formdata.createCommunication == null || formdata.createCommunication == false) && (formdata.createClarraDocs == null || formdata.createClarraDocs == false) && (formdata.createTimeslip == null || formdata.createTimeslip == false)) {
    console.log("No action selected, proceeding to send email.");
    event.completed({ allowEvent: true });
    return;
  }


  const storedValue = localStorage.getItem("uiDictionary");

  const uiDictionary = storedValue
    ? JSON.parse(storedValue)
    : getDefaultUiDictionary();

  if ((formdata.createCommunication == null || formdata.createCommunication == false) && (formdata.createClarraDocs == null || formdata.createClarraDocs == false) && (formdata.createTimeslip == null || formdata.createTimeslip == false)) {
    console.log("No action selected, proceeding to send email.");
    event.completed({ allowEvent: true });
    return true;
    // Continue send
  }
  //const formdata = localStorage.getItem("SelectedMatter");
  const matter = formdata.matter; // localStorage.getItem("SelectedMatter");

  //so far not used
  const Clarrato = localStorage.getItem("SelectedTo");

  const tags = localStorage.getItem("SelectedTags");

  if (formdata.createCommunication && (matter == null || matter == "" || matter == "null")) {
    return await Validation(event, false, `${uiDictionary.Matter} is not selected, Please select ${uiDictionary.Matter} from Log to Clarra Add-in.`);
  }
  const userProfilr = Office.context.mailbox.userProfile;
  const from = userProfilr.displayName + " (" + userProfilr.emailAddress + ")"
  const timezone = userProfilr.timeZone;
  const externalID = "";
  const conversationid = "";
  const internetMessageId = "";

  item = Office.context.mailbox.item;
  if (item.itemId) {
    externalID = item.itemId;
  }
  if (item.conversationID) {
    conversationid = item.conversationID;
  }

  if (item.internetMessageId) {
    internetMessageId = item.internetMessageId;
  }

  await item.subject.getAsync((asyncResult) => {
    if (asyncResult.status === Office.AsyncResultStatus.Failed) {
      return;
    }

    subject = asyncResult.value;
    subject = subject.replace("{matter}", matter);
    setSubject(subject);


    item.to.getAsync((asyncResult) => {
      if (asyncResult.status === Office.AsyncResultStatus.Failed) {
        return;
      }
      To = asyncResult.value;

      // Get CC recipients following the same pattern as TO
      item.cc.getAsync((ccAsyncResult) => {
        let Cc = null;
        console.log("CC retrieval status:", ccAsyncResult.status);
        if (ccAsyncResult.status === Office.AsyncResultStatus.Succeeded) {
          Cc = ccAsyncResult.value;
          console.log("CC recipients found:", Cc ? Cc.length : 0, Cc);
        } else {
          console.log("CC retrieval failed:", ccAsyncResult.error);
        }

        const selectedattachment1 = localStorage.getItem("attachments");
        const selectedattachment = sessionStorage.getItem("attachments");
        attachmentParam = null;
        if (selectedattachment) {
          attachmentParam = JSON.parse(selectedattachment) || [];
        }

        if (attachmentParam != null && attachmentParam.length > 0) {
          const fileAttachPromises = attachmentParam.map((attachment) => {
            return new Promise((resolve, reject) => {
              item.getAttachmentContentAsync(attachment.id, (result) => {
                try {
                  // Returns a file object, or null for attachments that
                  // can't be uploaded as bytes (cloud/URL, unknown formats).
                  resolve(buildAttachmentFile(result, attachment));
                } catch (error) {
                  reject(error.message || String(error));
                }
              });
            });
          });

          Promise.all(fileAttachPromises)
            .then((files) => {
              // Drop skipped attachments (nulls) before building the list.
              const validFiles = files.filter(Boolean);
              const distinctFiles = Array.from(
                new Map(validFiles.map(file => [file.filename, file])).values());
              return CallCommunicationSave(event, externalID, conversationid, subject, matter, tags, from, To, Cc, distinctFiles, timezone, formdata, internetMessageId, uiDictionary);
              // Do something with the files array
            })
            .catch((error) => {
              console.error('Error processing attachments:', error);
              return Validation(event, false, "Error processing attachments");
            });
        }
        else {
          return CallCommunicationSave(event, externalID, conversationid, subject, matter, tags, from, To, Cc, null, timezone, formdata, internetMessageId, uiDictionary);
        }
      });
    });
  }
  );
}


async function onMessageSendHandler(event) {
  console.log("onMessageSendHandler called");
  localStorage.setItem("composeloader", "0");
  console.log("onMessageSendHandler Common Request");
  const isClassicOutlook = localStorage.getItem("isClassicOutlook") === "1";
  const isMacOutlook = localStorage.getItem("isMacOutlook") === "1";
  const IsDektop = localStorage.getItem("hostName") === "1";



  const isPaneAlive = await new Promise(function (resolve) {
    const item = Office.context.mailbox.item;
    if (!item) {
      console.log("No mailbox item found.");
      resolve(false);
      return;
    }
    item.saveAsync(function (saveResult) {
      if (saveResult.status !== Office.AsyncResultStatus.Succeeded) {
        console.log("Save failed. Cannot get itemId.");
        resolve(false);
        return;
      }

      const currentItemId = saveResult.value;
      const challengeToken = "challenge-" + currentItemId + "-" + Date.now() + "-" + Math.random();
      console.log("Challenge Token Issued:", challengeToken);
      function handleResponse(e) {
        if (e.key === "onSendResponse" && e.newValue === challengeToken) {
          console.log("Challenge matched. Pane is alive.");
          cleanup();
          resolve(true);
        }
      }
      const timeoutId = setTimeout(function () {
        console.log("Pane did not respond within timeout.");
        cleanup();
        resolve(false);
      }, (!isMacOutlook) ? 500 : 1000);

      function cleanup() {
        clearTimeout(timeoutId);
        window.removeEventListener("storage", handleResponse);
        localStorage.removeItem("onSendChallenge");
        localStorage.removeItem("onSendResponse");
      }

      window.addEventListener("storage", handleResponse);
      localStorage.setItem("currentSendingItem", currentItemId);
      localStorage.setItem("onSendChallenge", challengeToken);

    });
  });




  if (!isMacOutlook) {
    if (!isPaneAlive) {
      // The pane did not respond. Send the email and do nothing else.
      localStorage.setItem("composeloader", "1");
      event.completed({ allowEvent: true });
      return;
    }
  }
  else {
    if (!isPaneAlive && !IsDektop) {
      localStorage.setItem("composeloader", "1");
      // The pane did not respond. Send the email and do nothing else.
      event.completed({ allowEvent: true });
      return;
    }
  }

  const storedValue = localStorage.getItem("uiDictionary");

  const uiDictionary = storedValue
    ? JSON.parse(storedValue)
    : getDefaultUiDictionary();

  var formdata = getLocalStorageItem("formData");
  //  console.log("formdata in API call:", JSON.stringify(formdata, null, 2));

  const modeC = localStorage.getItem("mode");

  // the below section is Specially for mac.
  if (isMacOutlook == true && IsDektop == true) {
    if (modeC == null || modeC == "0") {
      localStorage.setItem("composeloader", "1");
      event.completed({ allowEvent: true });
      return true;
    }
  }

  var isvalid = validateForm(formdata);
  formdata.isValid = isvalid;

  if (formdata.isValid == false && isMacOutlook == true && IsDektop == true) {
    localStorage.setItem("composeloader", "1");
    event.completed({ allowEvent: true });
    return true;
  }



  if (formdata.isValid == false) {
    localStorage.setItem("composeloader", "1");
    const msg = "Mandatory fields are required";
    Office.context.mailbox.item.notificationMessages.addAsync("validationError", {
      type: "errorMessage",
      message: msg
    });
    event.completed({ allowEvent: false });
    return true;
  }

  if ((formdata.createCommunication == null || formdata.createCommunication == false) && (formdata.createClarraDocs == null || formdata.createClarraDocs == false) && (formdata.createTimeslip == null || formdata.createTimeslip == false)) {
    console.log("No action selected, proceeding to send email.");
    localStorage.setItem("composeloader", "1");
    event.completed({ allowEvent: true });
    return true;

  }
  //const formdata = localStorage.getItem("SelectedMatter");
  const matter = formdata.matter; // localStorage.getItem("SelectedMatter");
  const Clarrato = localStorage.getItem("SelectedTo");

  const tags = localStorage.getItem("SelectedTags");

  if (formdata.createCommunication && (matter == null || matter == "" || matter == "null")) {
    return await Validation(event, false, `${uiDictionary.Matter} is not selected, Please select ${uiDictionary.Matter} from Log to Clarra Add-in.`);
  }
  const userProfilr = Office.context.mailbox.userProfile;
  const from = userProfilr.displayName + " (" + userProfilr.emailAddress + ")"
  const timezone = userProfilr.timeZone;
  const externalID = "";
  const conversationid = "";
  const internetMessageId = "";

  item = Office.context.mailbox.item;
  if (item.itemId) {
    externalID = item.itemId;
  }
  if (item.conversationID) {
    conversationid = item.conversationID;
  }

  if (item.internetMessageId) {
    internetMessageId = item.internetMessageId;
  }


  await item.subject.getAsync((asyncResult) => {
    if (asyncResult.status === Office.AsyncResultStatus.Failed) {
      return;
    }

    subject = asyncResult.value;
    subject = subject.replace("{matter}", matter);
    setSubject(subject);


    item.to.getAsync((asyncResult) => {
      if (asyncResult.status === Office.AsyncResultStatus.Failed) {
        return;
      }
      To = asyncResult.value;

      // Get CC recipients following the same pattern as TO
      item.cc.getAsync((ccAsyncResult) => {
        let Cc = null;
        console.log("CC retrieval status:", ccAsyncResult.status);
        if (ccAsyncResult.status === Office.AsyncResultStatus.Succeeded) {
          Cc = ccAsyncResult.value;
          console.log("CC recipients found:", Cc ? Cc.length : 0, Cc);
        } else {
          console.log("CC retrieval failed:", ccAsyncResult.error);
        }

        const selectedattachment1 = localStorage.getItem("attachments");
        const selectedattachment = sessionStorage.getItem("attachments");
        attachmentParam = null;
        if (selectedattachment) {
          attachmentParam = JSON.parse(selectedattachment) || [];
        }

        if (attachmentParam != null && attachmentParam.length > 0) {
          const fileAttachPromises = attachmentParam.map((attachment) => {
            return new Promise((resolve, reject) => {
              item.getAttachmentContentAsync(attachment.id, (result) => {
                try {
                  // Returns a file object, or null for attachments that
                  // can't be uploaded as bytes (cloud/URL, unknown formats).
                  resolve(buildAttachmentFile(result, attachment));
                } catch (error) {
                  reject(error.message || String(error));
                }
              });
            });
          });

          Promise.all(fileAttachPromises)
            .then((files) => {
              // Drop skipped attachments (nulls) before building the list.
              const validFiles = files.filter(Boolean);
              const distinctFiles = Array.from(
                new Map(validFiles.map(file => [file.filename, file])).values());
              return CallCommunicationSave(event, externalID, conversationid, subject, matter, tags, from, To, Cc, distinctFiles, timezone, formdata, internetMessageId, uiDictionary);
              // Can be use and process the files array
            })
            .catch((error) => {
              console.error('Error processing attachments:', error);
              return Validation(event, false, "Error processing attachments");
            });
        }
        else {
          return CallCommunicationSave(event, externalID, conversationid, subject, matter, tags, from, To, Cc, null, timezone, formdata, internetMessageId, uiDictionary);
        }
      });
    });
  }
  );
}

async function onMessageSendHandler_v1(event) {
  prepareDraftAndTagEmail();
  // LogandSend(event);
}
async function LogandSend(event) {

  var formdata = getLocalStorageItem("formData");
  const modeC = localStorage.getItem("mode");
  if (modeC == null || modeC == "0") {
    localStorage.setItem("composeloader", "1");
    event.completed({ allowEvent: true });
    return true;
  }
  else {
    var isvalid = validateForm(formdata);
    if (!isvalid) {
      localStorage.setItem("composeloader", "1");
      event.completed({ allowEvent: true });
      return true;
    }
    formdata.isValid = isvalid;
  }
  if (formdata.isValid == false) {
    msg = "Mandatory fields are required.";
    Office.context.mailbox.item.notificationMessages.addAsync("validationError", {
      type: "errorMessage",
      message: msg
    });
    localStorage.setItem("composeloader", "1");
    event.completed({ allowEvent: false });
    return true;
  }


  const mode = localStorage.getItem("mode");
  if (mode == null || mode == "0") {
    localStorage.setItem("composeloader", "1");
    event.completed({ allowEvent: true });
    return true;
  }
  var formdata = getLocalStorageItem("formData");
  if (formdata == null) {
    localStorage.setItem("composeloader", "1");
    event.completed({ allowEvent: true });
    return true;
  }
  if ((formdata.createCommunication == null || formdata.createCommunication == false) && (formdata.createClarraDocs == null || formdata.createClarraDocs == false) && (formdata.createTimeslip == null || formdata.createTimeslip == false)) {
    localStorage.setItem("composeloader", "1");
    event.completed({ allowEvent: true });
    return true;
    // Continue send
  }
  //const formdata = localStorage.getItem(LOCALSTORAGE_KEYS[DEFAULT_UI_DICTIONARY.SelectedMatter]);
  const matter = formdata.matter;

  //so far not used
  const Clarrato = localStorage.getItem("SelectedTo");

  const tags = localStorage.getItem("SelectedTags");

  if (formdata.createCommunication && (matter == null || matter == "" || matter == "null")) {
    return await Validation(event, false, "Matter is not selected, Please select matter from Log to Clarra Add-in.");
  }
  const userProfilr = Office.context.mailbox.userProfile;
  const from = userProfilr.displayName + " (" + userProfilr.emailAddress + ")"
  const timezone = userProfilr.timeZone;
  const externalID = "";
  const conversationid = "";
  item = Office.context.mailbox.item;
  if (item.itemId) {
    externalID = item.itemId;
  }
  if (item.conversationID) {
    conversationid = item.conversationID;
  }
  await item.subject.getAsync((asyncResult) => {
    if (asyncResult.status === Office.AsyncResultStatus.Failed) {
      return;
    }

    subject = asyncResult.value;
    subject = subject.replace("{matter}", matter);
    setSubject(subject);


    item.to.getAsync((asyncResult) => {
      if (asyncResult.status === Office.AsyncResultStatus.Failed) {
        return;
      }
      To = asyncResult.value;

      // Get CC recipients following the same pattern as TO
      item.cc.getAsync((ccAsyncResult) => {
        let Cc = null;
        console.log("CC retrieval status (2nd block):", ccAsyncResult.status);
        if (ccAsyncResult.status === Office.AsyncResultStatus.Succeeded) {
          Cc = ccAsyncResult.value;
          console.log("CC recipients found (2nd block):", Cc ? Cc.length : 0, Cc);
        } else {
          console.log("CC retrieval failed (2nd block):", ccAsyncResult.error);
        }

        const selectedattachment1 = localStorage.getItem("attachments");
        const selectedattachment = sessionStorage.getItem("attachments");
        attachmentParam = null;
        if (selectedattachment) {
          attachmentParam = JSON.parse(selectedattachment) || [];
        }

        if (attachmentParam != null && attachmentParam.length > 0) {
          const fileAttachPromises = attachmentParam.map((attachment) => {
            return new Promise((resolve, reject) => {
              item.getAttachmentContentAsync(attachment.id, (result) => {
                try {
                  // Returns a file object, or null for attachments that
                  // can't be uploaded as bytes (cloud/URL, unknown formats).
                  resolve(buildAttachmentFile(result, attachment));
                } catch (error) {
                  reject(error.message || String(error));
                }
              });
            });
          });

          Promise.all(fileAttachPromises)
            .then((files) => {
              // Drop skipped attachments (nulls) before building the list.
              const validFiles = files.filter(Boolean);
              const distinctFiles = Array.from(
                new Map(validFiles.map(file => [file.filename, file])).values());
              return CallCommunicationSave(event, externalID, conversationid, subject, matter, tags, from, To, Cc, distinctFiles, timezone, formdata);
              // Do something with the files array
            })
            .catch((error) => {
              console.error('Error processing attachments:', error);
              return Validation(event, false, "Error processing attachments");
            });
        }
        else {
          return CallCommunicationSave(event, externalID, conversationid, subject, matter, tags, from, To, Cc, null, timezone, formdata);

        }
      });
    });
  });
}

function prepareDraftAndTagEmail() {
  const ctx = Office.context.mailbox.item;

  function promisify(f) {
    return new Promise(function (resolve, reject) {
      f(function (result) {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          resolve(result.value);
        } else {
          reject(result.error);
        }
      });
    });
  }

  const TAGS = {
    commid: "20390",
    tenantid: "2342"
  };

  // Run steps
  return promisify(function (cb) {
    ctx.saveAsync(cb);
  })
    .then(function () {
      return Promise.all([
        promisify(function (cb) {
          ctx.getConversationIdAsync(cb);
        }),
        promisify(function (cb) {
          ctx.getItemIdAsync(cb);
        })
      ]);
    })
    .then(function (results) {
      const conversationId = results[0];
      const itemId = results[1];
      console.log("Conversation ID:", conversationId);
      console.log("Item ID:", itemId);

      return promisify(function (cb) {
        ctx.loadCustomPropertiesAsync(cb);
      }).then(function (customProps) {
        customProps.set("commid", TAGS.commid);
        customProps.set("tenantid", TAGS.tenantid);
        customProps.saveAsync();

        // Return metadata if you want to send it to an API
        return {
          matterName: localData.matterName,
          formType: localData.formType,
          tenantId: localData.tenantId,
          conversationId: conversationId,
          messageId: itemId,
          commid: TAGS.commid,
          tagTenantId: TAGS.tenantid
        };
      });
    })
    .catch(function (err) {
      console.error("Error in prepareDraftAndTagEmail:", err);
      throw err;
    });
}
function setSubject(newSubject) {
  Office.context.mailbox.item.subject.setAsync(newSubject, function (result) {
    if (result.status === Office.AsyncResultStatus.Failed) {
      console.error(result.error.message);
    }
  });
}
function appendBody(textToAppend) {
  Office.context.mailbox.item.body.appendOnSendAsync(textToAppend, { coercionType: Office.CoercionType.Html }, function (result) {
    if (result.status === Office.AsyncResultStatus.Failed) {
      console.error(result.error.message);
    }
  });
}

function setBody(newBody) {
  Office.context.mailbox.item.body.setAsync(newBody, { coercionType: Office.CoercionType.Html }, function (result) {
    if (result.status === Office.AsyncResultStatus.Failed) {
      console.error(result.error.message);
    }
  });
}

function CallCommunicationSave(event, externalID, conversationid, subject, matter, tags, from, To, Cc, attachments, timezone, EmailData, internetMessageId, uiDictionary) {
  EmailData = getLocalStorageItem("formData");
  const currentDateTime = new Date();
  const hours = currentDateTime.getHours();    // Hours (0-23)
  const minutes = currentDateTime.getMinutes(); // Minutes (0-59)
  const seconds = currentDateTime.getSeconds(); // Seconds (0-59)
  //const date = currentDateTime;
  const time = `${hours}:${minutes}:${seconds}`;

  item.body.getAsync(Office.CoercionType.Html, (result) => {
    let body = result.value;
    if (body == null || body.trim() == "")
      return Validation(event, false, "Clarra required email body");
    if (subject == null || (subject != null && subject.trim() == ""))
      return Validation(event, false, "Clarra required subject");
    let Recipients = [];
    if (To != null) {
      To.forEach((element) => {
        Recipients.push({
          contactID: 0,
          userID: "",
          fullNameWithEmail:
            element.displayName + " (" + element.emailAddress + ")",
          isContact: true,
          type: 1,
        });
      });
    }

    // Process CC recipients
    let CcRecipients = [];
    console.log("Processing CC recipients. Cc value:", Cc);
    if (Cc != null) {
      console.log("CC is not null, processing", Cc.length, "recipients");
      Cc.forEach((element) => {
        CcRecipients.push({
          contactID: 0,
          userID: "",
          fullNameWithEmail: element.displayName + " (" + element.emailAddress + ")",
          isContact: true,
          type: 2,
        });
      });
    }
    console.log("Final CcRecipients array:", CcRecipients);

    let sender = {
      contactID: 0,
      userID: "",
      fullNameWithEmail: from,
      isContact: true,
      type: 1,
    };
    console.log("About to create payload. CcRecipients:", CcRecipients);

    let PayloadData = {
      externalID: externalID,
      conversationID: conversationid,
      internetMessageID: internetMessageId,
      id: 0,
      communicationType: "Email",
      Subject: subject,
      date: currentDateTime,
      time: time,
      description: btoa(unescape(encodeURIComponent(body))),
      Matter: {
        MatterId: '',
        MatterWithNumber: matter,
      },
      sender: sender,
      recipients: Recipients,
      CC: CcRecipients,
      CommunicationTags: [
        {
          TagID: 0,
          TagName: tags,
        },
      ],

      UploadFileAttachments: (attachments != null) ? attachments : [],
      ServiceType: null,
      TimeZone: timezone, // "(UTC-08:00) Pacific Time (US & Canada)",
      CreateCommunication: EmailData.createCommunication,
      // UploadFileAttachments: EmailData.attachments,
      CreateClarraDocs: (EmailData.createClarraDocs == true) ? true : null,
      ClarraDocsStatus: null,

      onlydocs: (EmailData.createCommunication == true) ? false : true,
      ClarraDocs: (EmailData.createClarraDocs == true) ? handleSubmitNewClarraDocs(EmailData, subject) : null,
      TimeSlip: (EmailData.createTimeslip == true) ? handleSubmitNewTimeSlip(EmailData, subject) : null,
      CreateTimeSlip: EmailData.createTimeslip,

    };

    if (!EmailData.createCommunication && !EmailData.createClarraDocs && EmailData.createTimeslip) {

      PayloadData.Matter = {
        MatterId: 0,
        MatterWithNumber: EmailData.matterts
      };

    }
    if (!EmailData.createCommunication && EmailData.createClarraDocs) {

      PayloadData.Matter = {
        MatterId: 0,
        MatterWithNumber: EmailData.matterdoc
      };
    }

    result1 = SaveCommunicationDirect(PayloadData, event, uiDictionary)

  });
}
function handleSubmitNewTimeSlip(TimeSlipData) {

  const matter = TimeSlipData?.matterts;
  if (matter == null || matter.trim() == "")
    return Validation(event, false, "TimeSlip required matter");

  if (TimeSlipData.description == null || TimeSlipData.description == "") {
    TimeSlipData.description = subject;
  }
  if (TimeSlipData.timekeeper == null || TimeSlipData.timekeeper.trim() == "")
    return Validation(event, false, "TimeSlip required timekeeper");

  if (TimeSlipData.activityCode == null || TimeSlipData.activityCode.trim() == "")
    return Validation(event, false, "TimeSlip required Activity Code");

  if (TimeSlipData.taskCode == null || TimeSlipData.taskCode.trim() == "")
    return Validation(event, false, "TimeSlip required Task Code");


  let comm_id = 0;
  let PayloadData = {
    externalID: null,
    id: 0,
    date: TimeSlipData.datets,
    duration: (TimeSlipData.duration == "") ? 0.0 : TimeSlipData.duration,
    timekeeper: TimeSlipData.timekeeper,
    matter: TimeSlipData.matterts,
    type: TimeSlipData.activityCode,
    project: TimeSlipData.taskCode,
    description: TimeSlipData.description,
    notes: TimeSlipData.note,
    billable: TimeSlipData.billable,
    CommunicationID: comm_id
  };

  return PayloadData;
}
function handleSubmitNewClarraDocs(ClarraDocsData, subject) {

  // SetCurrentMode(3);
  const matter = ClarraDocsData?.matterdoc;
  if (matter == null || matter.trim() == "")
    return Validation(event, false, "ClarraDocs required matter");
  let status = ClarraDocsData?.status.label;
  if (status == null || status.trim() == "")
    status = "Sent";
  // return Validation(event, false,"ClarraDocs required status");
  let serviceType = ClarraDocsData?.serviceType.label;
  if (serviceType == null || serviceType.trim() == "")
    serviceType = "";
  //return Validation(event, false,"ClarraDocs required serviceType");

  const tags = ClarraDocsData?.tagdoc?.map((v) => v.label);
  if (tags == null || tags.length == 0)
    return Validation(event, false, "ClarraDocs required tags");

  if (ClarraDocsData.descriptiondoc == null || ClarraDocsData.descriptiondoc == "") {
    ClarraDocsData.descriptiondoc = subject;
  }

  const SendToDocketing = ClarraDocsData?.sendToDocketing;
  const docketingComplete = ClarraDocsData?.docketingComplete;
  const DocketingNote = ClarraDocsData?.docketingNotes;
  let PayloadData = {
    externalID: null,
    id: 0,
    Note: ClarraDocsData.notedoc,
    MailType: serviceType,
    MatterNumber: matter,
    Date: ClarraDocsData.datedoc,
    Status: status,
    Description: ClarraDocsData.descriptiondoc,
    Tags: tags,
    SendToDocketing: SendToDocketing,
    CompleteDocketing: docketingComplete,
    DocketingNote: DocketingNote,
    UploadFileAttachments: ClarraDocsData.attachmentsdoc,
    onlydocs: false
  };

  return PayloadData;
}

function base64ToFile(base64, fileName, mimeType) {
  //const blob = base64ToBlob(base64, mimeType);
  const blob = new Blob(
    [
      new Uint8Array(
        atob(base64)
          .split("")
          .map((char) => char.charCodeAt(0))
      ),
    ],
    { type: "application/octet-stream" }
  );
  return blob;
}

function emlToFile(emlString) {
  return new Blob([emlString], { type: "message/rfc822" });
}

function ensureEmlFilename(filename) {
  if (!filename) return "attached-email.eml";
  return /\.eml$/i.test(filename) ? filename : filename + ".eml";
}

// Calendar invite attachments come back in "ICalendar" format: a raw
// iCalendar (.ics) string. Wrap it in a Blob for upload.
function icsToFile(icsString) {
  return new Blob([icsString], { type: "text/calendar" });
}

function ensureIcsFilename(filename) {
  if (!filename) return "calendar-event.ics";
  return /\.ics$/i.test(filename) ? filename : filename + ".ics";
}

// Converts an Office getAttachmentContentAsync result into a
// { blobfile, filename } object ready for upload.
// Returns null when the attachment cannot be uploaded as file bytes
// (cloud/URL attachments or unknown formats) so the caller can skip it
// without failing the entire send.
// Throws only when the content could not be retrieved at all.
function buildAttachmentFile(result, attachment) {
  if (result.status !== Office.AsyncResultStatus.Succeeded) {
    throw new Error("Failed to get attachment content: " +
      (result.error ? result.error.message : "unknown error"));
  }

  const { content, format } = result.value;
  const Formats = Office.MailboxEnums.AttachmentContentFormat;

  switch (format) {
    case Formats.Base64:
      return {
        blobfile: base64ToFile(content, attachment.filename, null),
        filename: attachment.filename,
      };
    case Formats.Eml:
      // Outlook item attachment (.msg / embedded email)
      return {
        blobfile: emlToFile(content),
        filename: ensureEmlFilename(attachment.filename),
      };
    case Formats.ICalendar:
      // Calendar invite (.ics)
      return {
        blobfile: icsToFile(content),
        filename: ensureIcsFilename(attachment.filename),
      };
    case Formats.Url:
      // Cloud attachment (OneDrive/SharePoint). The content is a link,
      // not file bytes, so it can't be uploaded as a file. Skip it
      // instead of blocking the send.
      console.warn("Skipping cloud (URL) attachment:", attachment.filename, content);
      return null;
    default:
      console.warn("Skipping attachment with unsupported format:", format, attachment.filename);
      return null;
  }
}

function handleAttachmentsCallback(result) {
  // Parse string to be a url, an .eml file, a base64-encoded string, or an .icalendar file.
  switch (result.value.format) {
    case Office.MailboxEnums.AttachmentContentFormat.Base64:
      {
        let file = base64ToFile(
          content,
          attachment.name,
          attachment.contentType
        );
      }
      return file;
      // Handle file attachment.
      break;
    case Office.MailboxEnums.AttachmentContentFormat.Eml:
      // Handle email item attachment.
      break;
    case Office.MailboxEnums.AttachmentContentFormat.ICalendar:
      // Handle .icalender attachment.
      break;
    case Office.MailboxEnums.AttachmentContentFormat.Url:
      // Handle cloud attachment.
      break;
    default:
    // Handle attachment formats that are not supported.
  }
}

// --- Second inline block (previously inside <head> in index.html) ---

Office.initialize = function () {

  localStorage.removeItem("attachments");
  sessionStorage.removeItem("attachments");

  if (Office.context.mailbox && Office.context.mailbox.item) {
    AddAttachment();
    Office.context.mailbox.item.addHandlerAsync(
      Office.EventType.AttachmentsChanged,
      addAttachmentEventHandler
    );
  }

};

Office.onReady((info) => {
  if (info.host === Office.HostType.Outlook) {

  }
});

function onRecipientsChanged_legacy(eventArgs) {
  const item = Office.context.mailbox.item;

  if (item.getRecipientsAsync) {
    item.getRecipientsAsync(function (asyncResult) {
      if (asyncResult.status === Office.AsyncResultStatus.Succeeded) {
        const recipients = asyncResult.value;
      } else {
        console.error("Error retrieving recipients:", asyncResult.error.message);
      }
    });
  }
}

function AddAttachment() {
  if (Office.context.mailbox && Office.context.mailbox.item) {
    const item = Office.context.mailbox.item;
    if (item.getAttachmentsAsync) {
      item.getAttachmentsAsync((asyncResult) => {
        if (asyncResult.status === Office.AsyncResultStatus.Succeeded) {
          const attachments = asyncResult.value;
          if (attachments && attachments.length > 0) {
            const attachmentParamCompose = attachments.map((attachment) => {
              return {
                id: attachment.id,
                filename: attachment.name,
              };
            });
            if (attachmentParamCompose != null) {
              attachmentParamCompose.forEach(element => {
                attachmentParam.push(element);
              });

              sessionStorage.setItem("attachments", JSON.stringify(attachmentParam));
              localStorage.setItem("attachments", JSON.stringify(attachmentParam));
            }

          }
        } else {
          console.error("Error retrieving attachments:", asyncResult.error);
        }
      });
    }
  }
}

function addAttachmentEventHandler() {
  if (Office.context == null || Office.context.mailbox == null || Office.context.mailbox.item == null) {
    return;
  }
  Office.context.mailbox.item.addHandlerAsync(
    Office.EventType.AttachmentsChanged,
    (eventArgs) => {
      if (eventArgs.attachmentStatus == "added") {
        let newfile = {
          id: eventArgs.attachmentDetails.id,
          filename: eventArgs.attachmentDetails.name,
        };
        attachmentParam.push(newfile);
        sessionStorage.setItem("attachments", JSON.stringify(attachmentParam));
        localStorage.setItem("attachments", JSON.stringify(attachmentParam));
      }
      else {
        const selectedattachment = localStorage.getItem("attachments");
        attachmentParam = JSON.parse(selectedattachment) || [];

        if (attachmentParam != null && attachmentParam.length > 0) {
          let index = attachmentParam.findIndex(item => item.id == eventArgs.attachmentDetails.id);
          if (index !== -1) {
            attachmentParam.splice(index, 1);
            sessionStorage.setItem("attachments", JSON.stringify(attachmentParam));
            localStorage.setItem("attachments", JSON.stringify(attachmentParam));
          }
        }
      }
    }
  );
}

// --- Explicit global exposure --------------------------------------------
// This file is now imported from src/index.tsx and bundled by webpack into
// app.[hash].js, instead of being loaded via a standalone <script src> tag
// in index.html. A plain <script> tag made every top-level `function`
// declaration an implicit global (a property of `window`); a bundled module
// does not - webpack wraps this file's contents in its own module scope, so
// those same declarations are now private to that scope unless explicitly
// attached to `window`. Two names are looked up by name from outside this
// module and must stay global:
//   - onMessageSendHandler: looked up via the manifest's
//     FunctionName="onMessageSendHandler" (both the classic manifest's
//     legacy Events/ItemSend registration and the modern manifest's
//     LaunchEvent/OnMessageSend registration reference this exact name).
//   - CallfromPopup: called as `window.opener.CallfromPopup(code)` from
//     assets/callback.js, which runs in a separate popup window and can
//     only reach this one via `window`.
window.onMessageSendHandler = onMessageSendHandler;
window.CallfromPopup = CallfromPopup;
